import React, { createContext, useState, useEffect } from 'react';

// Create the progress context
export const ProgressContext = createContext();

// Create the progress provider component
export const ProgressProvider = ({ children }) => {
  // Initialize state for lesson progress
  const [progress, setProgress] = useState({
    completedLessons: [],
    completedExercises: [],
    lastVisited: null
  });

  // Load progress from localStorage when component mounts
  useEffect(() => {
    const savedProgress = localStorage.getItem('inkDrawingProgress');
    if (savedProgress) {
      try {
        setProgress(JSON.parse(savedProgress));
      } catch (error) {
        console.error('Error parsing progress data:', error);
        // If there's an error, initialize with empty progress
        setProgress({
          completedLessons: [],
          completedExercises: [],
          lastVisited: null
        });
      }
    }
  }, []);

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('inkDrawingProgress', JSON.stringify(progress));
  }, [progress]);

  // Function to mark a lesson as completed
  const completeLesson = (lessonId) => {
    setProgress(prevProgress => {
      // Only add the lesson if it's not already in the array
      if (!prevProgress.completedLessons.includes(lessonId)) {
        return {
          ...prevProgress,
          completedLessons: [...prevProgress.completedLessons, lessonId],
          lastVisited: lessonId
        };
      }
      return prevProgress;
    });
  };

  // Function to mark an exercise as completed
  const completeExercise = (exerciseId) => {
    setProgress(prevProgress => {
      // Only add the exercise if it's not already in the array
      if (!prevProgress.completedExercises.includes(exerciseId)) {
        return {
          ...prevProgress,
          completedExercises: [...prevProgress.completedExercises, exerciseId]
        };
      }
      return prevProgress;
    });
  };

  // Function to check if a lesson is completed
  const isLessonCompleted = (lessonId) => {
    return progress.completedLessons.includes(lessonId);
  };

  // Function to check if an exercise is completed
  const isExerciseCompleted = (exerciseId) => {
    return progress.completedExercises.includes(exerciseId);
  };

  // Function to set the last visited lesson
  const setLastVisited = (lessonId) => {
    setProgress(prevProgress => ({
      ...prevProgress,
      lastVisited: lessonId
    }));
  };

  // Function to reset all progress
  const resetProgress = () => {
    setProgress({
      completedLessons: [],
      completedExercises: [],
      lastVisited: null
    });
  };

  // Calculate overall course progress percentage
  const calculateOverallProgress = (totalLessons) => {
    if (totalLessons === 0) return 0;
    return Math.round((progress.completedLessons.length / totalLessons) * 100);
  };

  // Provide the context value to children
  return (
    <ProgressContext.Provider
      value={{
        progress,
        completeLesson,
        completeExercise,
        isLessonCompleted,
        isExerciseCompleted,
        setLastVisited,
        resetProgress,
        calculateOverallProgress
      }}
    >
      {children}
    </ProgressContext.Provider>
  );
};
